import React, { useRef, useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  Accessibility,
  Lightbulb,
  Support,
  TrendingUp,
  ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { useTranslation } from '../../hooks/useTranslation';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', isVisible }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isVisible) return;
    
    const startTime = Date.now();
    const endValue = parseInt(end.replace(/\D/g, ''));
    
    const timer = setInterval(() => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);
      
      setCount(currentCount);
      
      if (progress === 1) {
        clearInterval(timer);
        setCount(endValue);
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [end, duration, isVisible]);
  
  return (
    <span>
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const AboutSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  // Intersection Observer for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: <Accessibility sx={{ fontSize: 48, color: '#424242' }} />,
      title: t('about.values.accessibility.title'),
      description: t('about.values.accessibility.description'),
      accentColor: '#424242'
    },
    {
      icon: <Lightbulb sx={{ fontSize: 48, color: '#424242' }} />,
      title: t('about.values.simplicity.title'),
      description: t('about.values.simplicity.description'),
      accentColor: '#424242'
    },
    {
      icon: <Support sx={{ fontSize: 48, color: '#424242' }} />,
      title: t('about.values.support.title'),
      description: t('about.values.support.description'),
      accentColor: '#424242'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 48, color: '#424242' }} />,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description'),
      accentColor: '#424242'
    }
  ];

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  return (
    <Box
      sx={{
        py: { xs: 3, md: 5 },
        backgroundColor: '#f8f9fa',
        position: 'relative'
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              color: '#212121',
              mb: 2
            }}
          >
            {t('about.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              color: '#666',
              maxWidth: 700,
              mx: 'auto',
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            {t('about.subtitle')}
          </Typography>
        </Box>

        {/* Story Section - Article Style */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            mb: { xs: 4, md: 6 }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { xs: 'auto', md: 500 } }}>
            {/* Image - Mobile First, Desktop Right */}
            <Box
              sx={{
                width: { xs: '100%', md: '40%' },
                height: { xs: 250, md: 500 },
                position: 'relative',
                overflow: 'hidden',
                order: { xs: 1, md: 2 }
              }}
            >
              <Box
                component="img"
                src="/images/group-graphic-designers-smiling-sitting-together.jpg"
                alt="Notre équipe"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
            </Box>

            {/* Text Content - Mobile Second, Desktop Left */}
            <Box
              sx={{
                width: { xs: '100%', md: '60%' },
                p: { xs: 3, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                order: { xs: 2, md: 1 }
              }}
            >
              <Typography
                variant="h3"
                component="h3"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#212121',
                  mb: 3,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  lineHeight: 1.3
                }}
              >
                {t('about.story.title')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#555',
                  lineHeight: 1.8,
                  fontSize: { xs: '1.1rem', md: '1.2rem' },
                  mb: 3
                }}
              >
                {t('about.story.description')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  lineHeight: 1.7,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontStyle: 'italic'
                }}
              >
                {t('about.mission.description')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Mission Section - Article Style (Reversed) */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            mb: { xs: 4, md: 6 }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { xs: 'auto', md: 450 } }}>
            {/* Image - Mobile First, Desktop Left */}
            <Box
              sx={{
                width: { xs: '100%', md: '40%' },
                height: { xs: 250, md: 450 },
                position: 'relative',
                overflow: 'hidden',
                order: { xs: 1, md: 1 }
              }}
            >
              <Box
                component="img"
                src="/images/group-business-executives-discussing-laptop-their-des.jpg"
                alt="Notre mission"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
            </Box>

            {/* Text Content - Mobile Second, Desktop Right */}
            <Box
              sx={{
                width: { xs: '100%', md: '60%' },
                p: { xs: 3, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                order: { xs: 2, md: 2 }
              }}
            >
              <Typography
                variant="h3"
                component="h3"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#212121',
                  mb: 3,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  lineHeight: 1.3
                }}
              >
                {t('about.mission.title')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#555',
                  lineHeight: 1.8,
                  fontSize: { xs: '1.1rem', md: '1.2rem' },
                  mb: 3
                }}
              >
                Nous croyons que chaque barbier mérite des outils professionnels pour prospérer. Notre plateforme simplifie la gestion quotidienne tout en offrant une expérience client exceptionnelle.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  lineHeight: 1.7,
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}
              >
                De la prise de rendez-vous à la gestion des clients, nous accompagnons les barbiers et coiffeurs hommes dans leur transformation digitale.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Team Section - Full Width */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            position: 'relative'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { xs: 'auto', md: 400 } }}>
            {/* Image - Mobile First, Desktop Right */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                height: { xs: 250, md: 400 },
                position: 'relative',
                overflow: 'hidden',
                order: { xs: 1, md: 2 }
              }}
            >
              <Box
                component="img"
                src="/images/Bureau-team-sitting.jpg"
                alt="Notre équipe"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
            </Box>

            {/* Text Content - Mobile Second, Desktop Left */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                p: { xs: 3, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                order: { xs: 2, md: 1 }
              }}
            >
              <Typography
                variant="h3"
                component="h3"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#212121',
                  mb: 3,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  lineHeight: 1.3
                }}
              >
                {t('about.team.title')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#555',
                  lineHeight: 1.8,
                  fontSize: { xs: '1.1rem', md: '1.2rem' }
                }}
              >
                {t('about.team.description')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutSection;